import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import { X, UploadCloud, Image } from "lucide-react";
import { updateInspirationalVideo, uploadVideoFileInspirational, upload_thumbnail } from "../../requests";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer";
import { useDispatch } from "react-redux";
import { notifyError, notifySuccess } from "../../utils/helper";
import styles from "../../_css/ContenetManagement.module.css";

const EditVideoModal = ({ show, onHide, videoData, onUpdate }) => {
    const dispatch = useDispatch();
    const [subTitle, setSubTitle] = useState("");
    const [title, setTitle] = useState("");
    const [author, setAuthor] = useState("");
    const [duration, setDuration] = useState("");
    const [videoFile, setVideoFile] = useState(null);
    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [videoUrl, setVideoUrl] = useState("");
    const [uploadProgress, setUploadProgress] = useState(0);
    const [thumbnailUploadProgress, setThumbnailUploadProgress] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [thumbnailUploading, setThumbnailUploading] = useState(false);
    const [loading, setLoading] = useState(false);

    // Store initial data
    const [initialData, setInitialData] = useState(null);

    // Function to truncate long URLs for display
    const truncateUrl = (url, maxLength = 30) => {
        if (!url) return "No file selected";

        if (url.length <= maxLength) return url;

        // For file names, just show the beginning
        if (!url.includes('://')) {
            if (url.length > maxLength) {
                return url.substring(0, maxLength - 3) + '...';
            }
            return url;
        }

        // For URLs, show the domain and truncate the rest
        try {
            const urlObj = new URL(url);
            const domain = urlObj.hostname;
            const path = urlObj.pathname;

            if (domain.length + 10 >= maxLength) {
                return domain.substring(0, maxLength - 3) + '...';
            }

            const remainingChars = maxLength - domain.length - 3;
            if (path.length > remainingChars) {
                return `${domain}...${path.substring(path.length - remainingChars)}`;
            }

            return `${domain}${path}`;
        } catch {
            return url.substring(0, maxLength - 3) + '...';
        }
    };

    // Function to get file name from URL
    const getFileNameFromUrl = (url) => {
        if (!url) return "No file selected";

        // If it's a file name (no protocol), return it truncated
        if (!url.includes('://')) {
            return truncateUrl(url, 25);
        }

        // If it's a URL, try to extract the file name
        try {
            const urlObj = new URL(url);
            const pathname = urlObj.pathname;
            const fileName = pathname.split('/').pop();

            if (fileName && fileName.includes('.')) {
                return truncateUrl(fileName, 25);
            }

            return truncateUrl(urlObj.hostname, 25);
        } catch {
            return truncateUrl(url, 25);
        }
    };

    useEffect(() => {
        if (videoData && show) {
            setSubTitle(videoData.heading || "");
            setTitle(videoData.subHeading || "");
            setAuthor(videoData.speaker || "");
            setDuration(videoData.duration || "");
            setVideoFile(null);
            setThumbnailFile(null);
            setVideoUrl("");

            setInitialData({
                heading: videoData.heading || "",
                subHeading: videoData.subHeading || "",
                speaker: videoData.speaker || "",
                duration: videoData.duration || "",
                video_url: videoData.video_url || videoData.link || "",
                thumbnail: videoData.thumbnail || videoData.imageUrl || ""
            });
        }
    }, [videoData, show]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const validVideoTypes = ['video/mp4', 'video/mov', 'video/avi', 'video/mkv', 'video/webm'];
            if (!validVideoTypes.includes(file.type)) {
                notifyError("Please upload a valid video file (MP4, MOV, AVI, MKV, WEBM)");
                return;
            }

            const maxSize = 50 * 1024 * 1024;
            if (file.size > maxSize) {
                notifyError("File size must be less than 50MB");
                return;
            }

            setVideoFile(file);
            setVideoUrl(""); // Clear URL when file is selected
        }
    };

    const handleThumbnailChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
            if (!validImageTypes.includes(file.type)) {
                notifyError("Please upload a valid image file (JPEG, PNG, GIF, WEBP)");
                return;
            }

            const maxSize = 5 * 1024 * 1024;
            if (file.size > maxSize) {
                notifyError("Thumbnail size must be less than 5MB");
                return;
            }

            setThumbnailFile(file);
        }
    };

    const handleUrlChange = (e) => {
        setVideoUrl(e.target.value);
        setVideoFile(null); // Clear file when URL is entered
    };

    const triggerFileInput = () => document.getElementById("upload-video-edit").click();
    const triggerThumbnailInput = () => document.getElementById("upload-thumbnail-edit").click();

    const handleRemoveFile = () => {
        setVideoFile(null);
    };

    const handleRemoveThumbnail = () => {
        setThumbnailFile(null);
    };

    const handleUrlUpload = () => {
        if (!videoUrl.trim()) {
            notifyError("Please enter a URL");
            return;
        }

        // Validate URL format
        const urlPattern = /^(https?:\/\/)/;
        if (!urlPattern.test(videoUrl)) {
            notifyError("Please enter a valid URL starting with http:// or https://");
            return;
        }

        notifySuccess("URL added successfully!");
    };

    const validateForm = () => {
        if (!subTitle.trim()) {
            notifyError("Please enter a title");
            return false;
        }
        if (!title.trim()) {
            notifyError("Please enter a sub title");
            return false;
        }
        if (!author.trim()) {
            notifyError("Please enter author name");
            return false;
        }
        if (!duration.trim()) {
            notifyError("Please enter duration");
            return false;
        }
        if (!videoFile && !videoUrl.trim() && !initialData?.video_url) {
            notifyError("Please upload a video file or enter a URL");
            return false;
        }
        return true;
    };

    // Check if form has changes
    const hasFormChanges = () => {
        if (!initialData) return false;

        return (
            subTitle !== initialData.heading ||
            title !== initialData.subHeading ||
            author !== initialData.speaker ||
            duration !== initialData.duration ||
            videoFile !== null ||
            thumbnailFile !== null ||
            videoUrl.trim() !== ""
        );
    };

    const uploadThumbnail = async (file) => {
        try {
            setThumbnailUploading(true);
            setThumbnailUploadProgress(0);

            const thumbnailResponse = await upload_thumbnail(file);
            setThumbnailUploadProgress(100);

            return thumbnailResponse.blobUrl;
        } catch (error) {
            notifyError("Failed to upload thumbnail");
            throw error;
        } finally {
            setThumbnailUploading(false);
        }
    };

    const uploadVideo = async (file) => {
        try {
            setUploading(true);
            setUploadProgress(0);

            const uploadResult = await uploadVideoFileInspirational(file, (progress) => {
                setUploadProgress(progress);
            });

            setUploading(false);
            setUploadProgress(100);
            notifySuccess("Video uploaded successfully!");
            return uploadResult.blobUrl || uploadResult.url;
        } catch (uploadError) {
            setUploading(false);
            notifyError(uploadError.response?.data?.message || uploadError.message || "Video upload failed");
            throw uploadError;
        }
    };

    const handleSave = async (status) => {
        if (!validateForm()) return;

        // Check if there are any changes
        if (!hasFormChanges()) {
            notifySuccess("No changes detected. Video remains unchanged.");
            onHide();
            return;
        }

        try {
            dispatch(startLoader());
            setLoading(true);

            // Always use initial URLs by default
            let videoUrlToUse = initialData?.video_url;
            let thumbnailUrl = initialData?.thumbnail;

            // Upload new thumbnail ONLY if a new thumbnail file is selected
            if (thumbnailFile) {
                try {
                    thumbnailUrl = await uploadThumbnail(thumbnailFile);
                    notifySuccess("Thumbnail updated successfully!");
                } catch (thumbnailError) {
                    notifyError("Thumbnail upload failed, keeping existing thumbnail");
                    // Keep existing thumbnail URL if upload fails
                }
            }

            // Handle video source based on user input:
            if (videoFile) {
                // User selected a new video file - upload it
                try {
                    videoUrlToUse = await uploadVideo(videoFile);
                } catch (uploadError) {
                    // If video upload fails, stop the process
                    return;
                }
            } else if (videoUrl.trim()) {
                // User entered a URL - use it directly
                videoUrlToUse = videoUrl.trim();
                notifySuccess("URL will be used for the video");
            } else {
                // User didn't select a new file or enter URL - keep the existing video URL
                videoUrlToUse = initialData?.video_url;
            }

            const payload = {
                title: subTitle.trim(),
                sub_title: title.trim(),
                speaker: author.trim(),
                duration: duration.trim(),
                video_url: videoUrlToUse,
                thumbnail: thumbnailUrl,
                status: status === "Publish" ? "Publish" : "Draft"
            };

            const id = videoData.id;
            const updatedVideo = await updateInspirationalVideo(id, payload);

            notifySuccess(`Video ${status === "Draft" ? "saved as draft" : "published"} successfully!`);
            onUpdate && onUpdate(updatedVideo);
            onHide();
        } catch (err) {
            notifyError(err.response?.data?.message || err.message || `Failed to ${status === "Draft" ? "save draft" : "publish"} video`);
        } finally {
            dispatch(stopLoader());
            setLoading(false);
            setUploading(false);
        }
    };

    const handleClose = () => {
        setSubTitle("");
        setTitle("");
        setAuthor("");
        setDuration("");
        setVideoFile(null);
        setThumbnailFile(null);
        setVideoUrl("");
        setUploadProgress(0);
        setThumbnailUploadProgress(0);
        setUploading(false);
        setThumbnailUploading(false);
        setInitialData(null);
        onHide();
    };

    const isFormDisabled = loading || uploading || thumbnailUploading;

    return (
        <Modal show={show} onHide={handleClose} centered dialogClassName="modal-38">
            <Modal.Body className={isFormDisabled ? styles.editVideoModalDisabled : ""}>
                <div className={styles.editVideoModalContainer}>
                    {/* Header */}
                    <div className={styles.editVideoModalHeader}>
                        <span className={styles.editVideoModalTitle}>Edit Video</span>
                        <X onClick={handleClose} className={styles.editVideoModalCloseIcon} />
                    </div>

                    {/* Fields */}
                    <div className={styles.editVideoModalFormGroup}>
                        <label className={styles.editVideoModalLabel}>Title</label>
                        <input
                            type="text"
                            value={subTitle}
                            onChange={(e) => setSubTitle(e.target.value)}
                            className={styles.editVideoModalInput}
                            disabled={isFormDisabled}
                        />
                    </div>

                    <div className={styles.editVideoModalFormGroup}>
                        <label className={styles.editVideoModalLabel}>Sub Title</label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className={styles.editVideoModalInput}
                            disabled={isFormDisabled}
                        />
                    </div>

                    <div className={styles.editVideoModalFormGroup}>
                        <label className={styles.editVideoModalLabel}>Author</label>
                        <input
                            type="text"
                            value={author}
                            onChange={(e) => setAuthor(e.target.value)}
                            className={styles.editVideoModalInput}
                            disabled={isFormDisabled}
                        />
                    </div>

                    <div className={styles.editVideoModalRow}>
                        <div className={styles.editVideoModalColumn}>
                            <label className={styles.editVideoModalLabel}>Duration</label>
                            <input
                                type="text"
                                value={duration}
                                onChange={(e) => setDuration(e.target.value)}
                                className={styles.editVideoModalInput}
                                disabled={isFormDisabled}
                            />
                        </div>
                        <div className={styles.editVideoModalColumn}>
                            <label className={styles.editVideoModalLabel}>Upload Video</label>
                            <div
                                className={styles.editVideoModalUploadArea}
                                onClick={isFormDisabled ? undefined : triggerFileInput}
                            >
                                <span
                                    className={styles.editVideoModalFileName}
                                    title={videoFile ? videoFile.name : initialData?.video_url}
                                >
                                    {videoFile
                                        ? truncateUrl(videoFile.name, 25)
                                        : getFileNameFromUrl(initialData?.video_url)
                                    }
                                </span>
                                {videoFile && (
                                    <X
                                        size={16}
                                        onClick={handleRemoveFile}
                                        className={styles.editVideoModalRemoveFile}
                                    />
                                )}
                            </div>
                            <input
                                type="file"
                                accept="video/*"
                                onChange={handleFileChange}
                                style={{ display: 'none' }}
                                id="upload-video-edit"
                                disabled={isFormDisabled}
                            />

                            {uploading && (
                                <div className={styles.editVideoModalProgressContainer}>
                                    <div
                                        className={styles.editVideoModalProgressBar}
                                        style={{ width: `${uploadProgress}%` }}
                                    ></div>
                                </div>
                            )}

                            {uploadProgress === 100 && !uploading && (
                                <p className={styles.editVideoModalSuccessMessage}>✅ Video uploaded successfully</p>
                            )}
                        </div>
                    </div>

                    {/* URL Upload Section */}
                    {/* <div className={styles.editVideoModalFormGroup}>
                        <label className={styles.editVideoModalLabel}>or Update Video URL</label>
                        <div className={styles.editVideoModalUrlContainer}>
                            <input
                                type="text"
                                placeholder="https://www.example.com/video.mp4"
                                value={videoUrl}
                                onChange={handleUrlChange}
                                className={styles.editVideoModalUrlInput}
                                disabled={isFormDisabled || !!videoFile}
                            />

                        </div>

                    </div> */}
                    <div className={styles.addInspirationalFormGroup}>
                        <label className={styles.addInspirationalLabel}>or Upload the URL</label>
                        <div className={styles.addInspirationalUrlContainer}>
                            <input
                                type="text"
                                placeholder="https://www.example.com/video.mp4"
                                value={videoUrl}
                                onChange={handleUrlChange}
                                className={styles.addInspirationalUrlInput}
                                disabled={isFormDisabled || !!videoFile}
                            />
                            {/* <button
                                className={styles.editVideoModalUploadButton}
                                onClick={handleUrlUpload}
                                disabled={isFormDisabled || !videoUrl.trim() || !!videoFile}
                            >
                                Update URL
                            </button> */}
                            {/* {initialData?.video_url && !videoUrl && !videoFile && (
                            <p className={styles.editVideoModalCurrentUrl}>
                                Current URL: {truncateUrl(initialData.video_url, 40)}
                            </p>
                        )} */}

                        </div>
                    </div>

                    {/* Thumbnail Section */}
                    <div className={styles.editVideoModalFormGroup}>
                        <label className={styles.editVideoModalLabel}>
                            Thumbnail {thumbnailUploading && "(Uploading...)"}
                        </label>

                        {/* Current Thumbnail Preview */}
                        {(initialData?.thumbnail || videoData?.imageUrl) && !thumbnailFile && (
                            <div className={styles.editVideoModalCurrentThumbnail}>
                                <label className={styles.editVideoModalThumbnailLabel}>Current Thumbnail:</label>
                                <div className={styles.editVideoModalThumbnailPreview}>
                                    <img
                                        src={initialData?.thumbnail || videoData.imageUrl}
                                        alt="Current thumbnail"
                                        className={styles.editVideoModalThumbnailImage}
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                        }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Thumbnail Upload */}
                        <div className={styles.editVideoModalThumbnailContainer}>
                            {thumbnailFile ? (
                                <div className={styles.editVideoModalThumbnailPreview}>
                                    <img
                                        src={URL.createObjectURL(thumbnailFile)}
                                        alt="New thumbnail preview"
                                        className={styles.editVideoModalThumbnailImage}
                                    />
                                    <div className={styles.editVideoModalThumbnailActions}>
                                        <span
                                            className={styles.editVideoModalFileName}
                                            title={thumbnailFile.name}
                                        >
                                            {truncateUrl(thumbnailFile.name, 25)}
                                        </span>
                                        <X
                                            size={16}
                                            onClick={handleRemoveThumbnail}
                                            className={styles.editVideoModalRemoveThumbnail}
                                            disabled={isFormDisabled}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div
                                    className={styles.editVideoModalUploadArea}
                                    onClick={isFormDisabled ? undefined : triggerThumbnailInput}
                                >
                                    <Image size={20} color="#000000" />
                                    <span className={styles.editVideoModalUploadText}>
                                        {(initialData?.thumbnail || videoData?.imageUrl) ? "Change Thumbnail" : "Upload Thumbnail"}
                                    </span>
                                </div>
                            )}
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleThumbnailChange}
                                style={{ display: 'none' }}
                                id="upload-thumbnail-edit"
                                disabled={isFormDisabled}
                            />
                        </div>

                        {thumbnailUploading && (
                            <div className={styles.editVideoModalProgressContainer}>
                                <div
                                    className={styles.editVideoModalProgressBar}
                                    style={{ width: `${thumbnailUploadProgress}%` }}
                                ></div>
                            </div>
                        )}

                        <p className={styles.editVideoModalHelpText}>
                            Recommended: 16:9 aspect ratio, max 5MB (JPEG, PNG, GIF, WEBP)
                        </p>
                    </div>

                    {/* Buttons */}
                    <div className={styles.editVideoModalButtonContainer}>
                        <button
                            className={styles.editVideoModalDraftButton}
                            onClick={() => handleSave("Draft")}
                            disabled={isFormDisabled}
                        >
                            {isFormDisabled ? "Saving..." : "Save As Draft"}
                        </button>
                        <button
                            className={styles.editVideoModalPublishButton}
                            onClick={() => handleSave("Publish")}
                            disabled={isFormDisabled}
                        >
                            {isFormDisabled ? "Publishing..." : "Publish"}
                        </button>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default EditVideoModal;